<template>
  <AdminLayout>
    <Head title="Packages" />

    <template #pageTitle> Packages </template>

    <template #pageTitleWidgets>
      <DataGridControlBar />
    </template>

    <template #headingOptions>
      <Link
        as="button"
        :href="createPackageRoute"
        type="button"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <PlusSmIconSolid
          class="-ml-0.5 mr-2 h-4 w-4"
          aria-hidden="true"
        />
        Add Package
      </Link>
    </template>

    <DataGrid
      name="packages_data_table"
      :endpoint="route('api.tables.packages.index')"
      :filters="form"
      :columns="columns"
      :block-size="40"
      :page-size="40"
    />
  </AdminLayout>
</template>

<script setup>
import { useInitGridState } from '@/Components/DataGrid/Composables/grid-state'
useInitGridState()
</script>

<script>
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { ChevronDownIcon } from '@heroicons/vue/solid'
import { Head, Link } from '@inertiajs/vue3'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import SelectMenu from '@/Components/Menus/SelectMenu.vue'
import { PlusSmIcon as PlusSmIconSolid } from '@heroicons/vue/solid'
import { PlusSmIcon as PlusSmIconOutline } from '@heroicons/vue/outline'
import DataGrid from '@/Components/DataGrid/Tables/Generic.vue'
import LinkCell from '@/Components/DataGrid/Cells/LinkCell.vue'
import DateCell from '@/Components/DataGrid/Cells/DateCell.vue'
import DollarCell from '@/Components/DataGrid/Cells/DollarCell.vue'
import PublishStatusCell from '@/Components/DataGrid/Cells/PublishStatusCell.vue'
import PercentCell from '@/Components/DataGrid/Cells/PercentCell.vue'
import DataGridControlBar from '@/Components/DataGrid/Controls/ControlBar.vue'

export default {
  components: {
    Link,
    AdminLayout,
    Head,
    SmallModal,
    ChevronDownIcon,
    SelectMenu,
    PlusSmIconOutline,
    PlusSmIconSolid,
    DataGrid,
    DateCell,
    DollarCell,
    LinkCell,
    PublishStatusCell,
    PercentCell,
    DataGridControlBar
  },

  props: {
    /**
     * laravel model for current company
     */
    company: {
      type: Object,
      required: true
    },
    categories: {
      type: Array,
      required: true
    },
    publishStatuses: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      form: {},
      columns: [
        {
          headerName: 'Name',
          field: 'name',
          cellRenderer: 'LinkCell',
          cellRendererParams: (params) => ({
            url: `/packages/${params.data.id}`,
            display: params.value
          }),
          filter: 'agTextColumnFilter',
          flex: 1,
          minWidth: 320
        },
        {
          headerName: 'Batch #',
          field: 'batch_num',
          filter: 'agTextColumnFilter',
          flex: 1,
          minWidth: 320
        },
        {
          headerName: 'Total Qty',
          field: 'quantity',
          width: 120
        },
        {
          headerName: 'Available Qty',
          field: 'quantity_available',
          width: 120
        },
        {
          headerName: 'Total THC',
          field: 'total_thc',
          filter: 'agNumberColumnFilter',
          cellRenderer: 'PercentCell'
        },
        {
          headerName: 'Product Category',
          field: 'product_category',
          filter: 'agSetColumnFilter',
          filterParams: {
            values: this.categories
          },
          suppressHeaderMenuButton: true
        },
        {
          headerName: 'Publish Date',
          field: 'published_at',
          filter: 'agDateColumnFilter',
          cellRenderer: 'DateCell',
          cellRendererParams: {
            formatter: 'MM/DD/YYYY'
          }
        },
        {
          headerName: 'Status',
          field: 'publish_status',
          cellRenderer: 'PublishStatusCell',
          sortable: false,
          filter: 'agSetColumnFilter',
          filterParams: {
            values: this.publishStatuses
          }
        }
      ]
    }
  },

  computed: {
    /**
     * Returns a list of packages to display
     * @returns {*}
     */
    packageList() {
      return this.packages?.data
    },

    /**
     * Returns a function that when passed a package model, will return the correct url to view that package in
     * the browser.
     */
    showPackageRoute() {
      // A better argument name here would be 'package' but thats a reserved word in javascript
      return (item) => {
        return route('packages.show', { package: item.id })
      }
    },

    /**
     * Returns a URL we can use to make a new package
     * @returns {*}
     */
    createPackageRoute() {
      return route('packages.new')
    }
  },

  methods: {
    editRow(item) {
      alert('EDIT ' + item.product)
    }
  }
}
</script>
